import React from "react";
import { Check, X, Shield, ArrowRight, CircleDollarSign, RefreshCw } from "lucide-react";
import { DECK } from "@/data/pitchContent";
import PhoneMock from "@/components/PhoneMock";
import { ASSETS } from "@/data/content";
import { Kicker, Headline, Mark, FlowRow, Avatar, SoftCard, SlideMeta, DeckLogo } from "@/pages/pitch/SlideKit";

const Pad = ({ children, className = "", style = {} }) => (
  <div className="absolute inset-0 px-20 pt-16 pb-20 flex flex-col" style={style}>
    <div className={`flex-1 min-h-0 ${className}`}>{children}</div>
  </div>
);

const Bg = ({ color }) => <div className="absolute inset-0" style={{ background: color }} />;
const Blob = ({ className, style }) => <div className={`absolute rounded-full blur-3xl ${className}`} style={style} />;

/* ============================== SLIDE 1 — COVER ============================== */
const Cover = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Blob className="h-[420px] w-[420px]" style={{ background: DECK.accent, opacity: 0.22, top: -120, right: 260 }} />
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 min-h-0 grid grid-cols-[1.15fr_0.85fr]">
        <div className="pl-20 pr-12 pt-14 flex flex-col">
          <DeckLogo height={46} />
          <div className="flex-1 flex flex-col justify-center">
            <Kicker>Investor Deck</Kicker>
            <h2 className="font-display font-extrabold tracking-tight leading-[0.95] mt-6 text-[104px]" style={{ color: DECK.ink }}>
              Workpay
            </h2>
            <p className="mt-6 text-[25px] font-semibold leading-snug max-w-[560px]" style={{ color: DECK.charcoal }}>
              Giving power back to service vendors while keeping <Mark>customers safe.</Mark>
            </p>
            <p className="mt-5 text-[17px] font-medium" style={{ color: DECK.muted }}>{s.support}</p>
          </div>
        </div>
        <div className="relative flex items-center justify-center pt-8 pb-4 pr-14 pl-2">
          <div className="relative w-full aspect-[6/7] rounded-[2.2rem] overflow-hidden" style={{ boxShadow: "0 40px 90px -40px rgba(11,11,15,0.5)" }}>
            <img src={s.image} alt={s.imageAlt} className="w-full h-full object-cover" style={{ objectPosition: "50% 50%", transform: "scale(1.08)" }} />
            <div className="absolute left-5 top-5 rounded-full px-4 py-2 text-[13px] font-bold flex items-center gap-2" style={{ background: "rgba(255,255,255,0.92)", color: DECK.ink }}>
              <Shield className="h-4 w-4" style={{ color: DECK.accentDeep }} /> Protected payment
            </div>
            <div className="absolute left-5 bottom-5 rounded-2xl px-5 py-3.5" style={{ background: "rgba(255,255,255,0.94)" }}>
              <p className="text-[12px] font-semibold" style={{ color: DECK.muted }}>Vendor payout</p>
              <p className="font-display text-[24px] font-extrabold" style={{ color: DECK.ink }}>AED 2,450</p>
            </div>
          </div>
        </div>
      </div>
      {/* Full-width journey strip */}
      <div className="shrink-0 px-20 py-6 flex items-center gap-6" style={{ background: "#fff", borderTop: "1px solid rgba(11,11,15,0.08)" }}>
        <span className="text-[12px] font-bold uppercase tracking-[0.2em] whitespace-nowrap" style={{ color: DECK.muted }}>How a project flows</span>
        <div className="flex-1 flex justify-center">
          <FlowRow steps={s.flow} highlightIndex={1} />
        </div>
      </div>
    </div>
  </>
);

/* ============================== SLIDE 2 — PROBLEM ============================== */
const Problem = ({ s }) => (
  <>
    <Bg color="#fff" />
    <Pad className="grid grid-cols-[0.92fr_1.08fr] gap-16">
      <div className="flex flex-col">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-6 text-[52px]">{s.headline}</Headline>
        <p className="mt-5 text-[19px] font-medium" style={{ color: DECK.muted }}>{s.subheadline}</p>
        <div className="mt-auto">
          <p className="font-display text-[28px] font-extrabold leading-tight" style={{ color: DECK.ink }}>
            WorkPay gives vendors a <Mark>better default.</Mark>
          </p>
          <p className="mt-3 text-[16px] font-medium max-w-[420px]" style={{ color: DECK.muted }}>{s.support}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 content-center">
        {s.quotes.map((q, i) => (
          <div key={q} className="rounded-2xl px-6 py-5" style={{ background: DECK.grey, borderLeft: `4px solid ${DECK.accent}` }}>
            <p className="text-[19px] font-bold italic leading-snug" style={{ color: DECK.charcoal }}>“{q}”</p>
          </div>
        ))}
      </div>
    </Pad>
  </>
);

/* ============================== SLIDE 3 — CASE STUDY ============================== */
const CaseStudy = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <div className="absolute inset-0 grid grid-cols-[0.82fr_1.18fr]">
      <div className="relative m-10 rounded-[2.2rem] overflow-hidden" style={{ boxShadow: "0 40px 90px -45px rgba(11,11,15,0.55)" }}>
        <img src={s.image} alt={s.imageAlt} className="w-full h-full object-cover" style={{ objectPosition: "50% 22%" }} />
        <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: "linear-gradient(to top, rgba(11,11,15,0.7), transparent)" }} />
        <span className="absolute left-5 bottom-5 text-[14px] font-bold text-white">36Hour Company</span>
      </div>
      <div className="px-16 py-16 flex flex-col justify-center">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-5 text-[44px] max-w-[560px]">{s.headline}</Headline>
        <div className="mt-8 flex items-end gap-4">
          <span className="font-display font-extrabold leading-none text-[90px]" style={{ color: DECK.ink }}>USD 30,000</span>
          <span className="font-display font-extrabold leading-none text-[64px]" style={{ color: DECK.accentDeep }}>+</span>
        </div>
        <div className="h-[6px] w-[260px] rounded-full mt-3" style={{ background: DECK.accent }} />
        <p className="mt-5 text-[19px] font-semibold" style={{ color: DECK.charcoal }}>{s.subheadline}</p>
        <div className="mt-6 flex flex-wrap gap-2.5">
          {s.costs.map((c) => (
            <span key={c} className="rounded-full px-4 py-2 text-[14px] font-bold" style={{ background: "#fff", color: DECK.ink, border: "1px solid rgba(11,11,15,0.1)" }}>{c}</span>
          ))}
        </div>
        <p className="mt-6 text-[15px] font-medium max-w-[520px]" style={{ color: DECK.muted }}>{s.small}</p>
      </div>
    </div>
  </>
);

/* ============================== SLIDE 4 — MARKET PAIN ============================== */
const MarketPain = ({ s }) => {
  const [hero, ...rest] = s.stats;
  return (
    <>
      <Bg color="#fff" />
      <Pad className="flex flex-col">
        <div className="flex items-end justify-between">
          <div>
            <Kicker>{s.kicker}</Kicker>
            <Headline className="mt-5 text-[46px]">This is not rare. <Mark>It is normal.</Mark></Headline>
          </div>
          <p className="text-[17px] font-medium max-w-[360px] text-right" style={{ color: DECK.muted }}>{s.subheadline}</p>
        </div>
        <div className="mt-8 flex-1 grid grid-cols-[1fr_1.6fr] gap-5">
          <div className="rounded-3xl px-9 py-8 flex flex-col justify-center" style={{ background: DECK.ink }}>
            <span className="font-display font-extrabold leading-none text-[88px]" style={{ color: DECK.accent }}>{hero.value}</span>
            <p className="mt-4 text-[17px] font-semibold leading-snug text-white">{hero.label}</p>
            <p className="mt-3 text-[12px] font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>{hero.source}</p>
          </div>
          <div className="grid grid-cols-2 grid-rows-2 gap-5">
            {rest.map((st) => (
              <SoftCard key={st.label} className="px-7 py-6 flex flex-col justify-center">
                <span className="font-display font-extrabold leading-none text-[46px]" style={{ color: DECK.ink }}>{st.value}</span>
                <p className="mt-2.5 text-[14px] font-semibold leading-snug" style={{ color: DECK.charcoal }}>{st.label}</p>
                <p className="mt-2 text-[11px] font-medium" style={{ color: DECK.muted }}>{st.source}</p>
              </SoftCard>
            ))}
          </div>
        </div>
        <div className="mt-7 flex items-center gap-3">
          <p className="font-display text-[24px] font-extrabold" style={{ color: DECK.muted }}>{s.bottom[0]}</p>
          <p className="font-display text-[24px] font-extrabold" style={{ color: DECK.ink }}>It is a <Mark>trust issue.</Mark></p>
        </div>
      </Pad>
    </>
  );
};

/* ============================== SLIDE 5 — CORE TRUTH ============================== */
const FearCard = ({ data, emphasized }) => (
  <div
    className="rounded-3xl px-8 py-7 flex flex-col"
    style={
      emphasized
        ? { background: DECK.ink, boxShadow: "0 30px 70px -30px rgba(11,11,15,0.5)" }
        : { background: "#fff", border: "1px solid rgba(11,11,15,0.1)" }
    }
  >
    <div className="flex items-center gap-4">
      <div className="h-14 w-14 rounded-2xl overflow-hidden shrink-0">
        <img src={data.image} alt={data.title} className="w-full h-full object-cover" style={{ objectPosition: "50% 20%" }} />
      </div>
      <h3 className="font-display text-[24px] font-extrabold" style={{ color: emphasized ? "#fff" : DECK.ink }}>{data.title}</h3>
    </div>
    <ul className="mt-5 space-y-3">
      {data.points.map((p) => (
        <li key={p} className="flex items-start gap-3 text-[17px] font-semibold leading-snug" style={{ color: emphasized ? "rgba(255,255,255,0.92)" : DECK.charcoal }}>
          <span className="mt-2 h-2 w-2 rounded-full shrink-0" style={{ background: DECK.accent }} />
          {p}
        </li>
      ))}
    </ul>
  </div>
);

const CoreTruth = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Pad className="flex flex-col">
      <div className="text-center">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-4 text-[42px]">
          {s.headline[0]} <span style={{ color: DECK.muted }}>{s.headline[1]}</span>
        </Headline>
        <p className="mt-3 text-[17px] font-medium" style={{ color: DECK.muted }}>{s.subheadline}</p>
      </div>
      <div className="mt-7 flex-1 grid grid-cols-2 gap-6 items-stretch">
        <FearCard data={s.customer} emphasized={false} />
        <FearCard data={s.vendor} emphasized />
      </div>
      <p className="mt-6 text-center font-display text-[22px] font-extrabold" style={{ color: DECK.ink }}>
        Today, the vendor often works <Mark>at the mercy of the customer.</Mark>
      </p>
    </Pad>
  </>
);

/* ============================== SLIDE 6 — SOLUTION ============================== */
const Solution = ({ s }) => (
  <>
    <Bg color="#fff" />
    <Blob className="h-[360px] w-[360px]" style={{ background: DECK.accent, opacity: 0.18, bottom: -100, left: 380 }} />
    <Pad className="grid grid-cols-[1.05fr_0.95fr] gap-14">
      <div className="flex flex-col">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-5 text-[46px]">Put a trusted party <Mark>in the middle.</Mark></Headline>
        <p className="mt-4 font-display text-[22px] font-extrabold" style={{ color: DECK.charcoal }}>{s.subheadline}</p>
        <div className="mt-6 grid grid-cols-1 gap-2.5">
          {s.points.map((p) => (
            <div key={p} className="flex items-start gap-3 text-[16px] font-semibold" style={{ color: DECK.charcoal }}>
              <Check className="h-5 w-5 mt-0.5 shrink-0" style={{ color: DECK.accentDeep }} /> {p}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col justify-center">
        <div className="rounded-[2rem] p-8" style={{ background: DECK.offwhite, border: "1px solid rgba(11,11,15,0.08)" }}>
          <div className="flex items-center justify-between">
            {s.flow.map((node, i) => (
              <React.Fragment key={node}>
                <div className="flex flex-col items-center gap-2.5">
                  <div
                    className="h-24 w-24 rounded-3xl flex items-center justify-center font-display text-[15px] font-extrabold text-center px-2"
                    style={i === 1 ? { background: DECK.accent, color: DECK.ink } : { background: "#fff", color: DECK.ink, border: "1px solid rgba(11,11,15,0.12)" }}
                  >
                    {node}
                  </div>
                </div>
                {i < s.flow.length - 1 && <ArrowRight className="h-7 w-7 shrink-0" style={{ color: DECK.accentDeep }} />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-8 pt-6 space-y-1.5" style={{ borderTop: "1px solid rgba(11,11,15,0.08)" }}>
            {s.bottom.map((b) => (
              <p key={b} className="font-display text-[22px] font-extrabold" style={{ color: DECK.ink }}>{b}</p>
            ))}
          </div>
        </div>
      </div>
    </Pad>
  </>
);

/* ============================== SLIDE 7 — HOW IT WORKS ============================== */
const HowItWorks = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Pad className="flex flex-col">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-5 text-[48px]">
        {s.headline[0]} <span style={{ color: DECK.accentDeep }}>{s.headline[1]}</span>
      </Headline>
      <div className="flex-1 flex items-center">
        <div className="relative w-full">
          {/* connector line sits at the centre of the number row, behind the circles */}
          <div className="absolute left-[8.33%] right-[8.33%] top-[27px] h-[3px] rounded-full z-0" style={{ background: "rgba(11,11,15,0.12)" }} />
          <div className="grid grid-cols-6 gap-5">
            {s.steps.map((step, i) => (
              <div key={step} className="relative flex flex-col items-center text-center">
                <span
                  className="relative z-10 h-14 w-14 rounded-full flex items-center justify-center font-display text-[20px] font-extrabold"
                  style={{ background: DECK.accent, color: DECK.ink, boxShadow: `0 0 0 6px ${DECK.offwhite}` }}
                >
                  {i + 1}
                </span>
                <div className="mt-7 px-4 py-6 w-full min-h-[150px] flex items-center justify-center rounded-2xl" style={{ background: "#fff", border: "1px solid rgba(11,11,15,0.1)" }}>
                  <p className="text-[15px] font-bold leading-snug" style={{ color: DECK.charcoal }}>{step}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Pad>
  </>
);

/* ============================== SLIDE 8 — PRODUCT ============================== */
const Product = ({ s }) => (
  <>
    <Bg color="#fff" />
    <Pad className="flex flex-col">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-5 text-[44px] max-w-[820px]">
        One link. Clear milestones. <Mark>Protected payouts.</Mark>
      </Headline>
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-start justify-center gap-3">
          {s.screens.map((sc) => (
            <div key={sc.variant} className="flex flex-col items-center">
              <div style={{ width: 174, height: 274, overflow: "hidden" }}>
                <div style={{ transform: "scale(0.58)", transformOrigin: "top left", width: 300 }}>
                  <PhoneMock variant={sc.variant} />
                </div>
              </div>
              <p className="mt-2 text-[13px] font-bold text-center max-w-[150px] leading-tight" style={{ color: DECK.charcoal }}>{sc.label}</p>
            </div>
          ))}
        </div>
      </div>
    </Pad>
  </>
);

/* ============================== SLIDE 9 — BUSINESS MODEL ============================== */
const BusinessModel = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Pad className="grid grid-cols-[1.2fr_0.8fr] gap-14">
      <div className="flex flex-col">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-5 text-[46px]">Revenue built <Mark>into the flow.</Mark></Headline>
        <div className="mt-8 grid grid-cols-2 gap-4 flex-1 content-center">
          {s.cards.map((c, i) => (
            <SoftCard key={c} className="px-6 py-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: DECK.accent }}>
                <CircleDollarSign className="h-5 w-5" style={{ color: DECK.ink }} />
              </div>
              <p className="text-[17px] font-bold leading-snug" style={{ color: DECK.ink }}>{c}</p>
            </SoftCard>
          ))}
        </div>
      </div>
      <div className="rounded-[2rem] px-8 py-8 flex flex-col justify-center" style={{ background: DECK.ink }}>
        <p className="text-[13px] font-bold uppercase tracking-[0.22em]" style={{ color: DECK.accent }}>Growth levers</p>
        <ul className="mt-5 space-y-4">
          {s.levers.map((l) => (
            <li key={l} className="flex items-center gap-3 text-[18px] font-bold text-white">
              <ArrowRight className="h-5 w-5 shrink-0" style={{ color: DECK.accent }} /> {l}
            </li>
          ))}
        </ul>
      </div>
    </Pad>
  </>
);

/* ============================== SLIDE 10 — GROWTH LOOP ============================== */
const GrowthLoop = ({ s }) => {
  const R = 210, cx = 300, cy = 250;
  return (
    <>
      <Bg color="#fff" />
      <Pad className="grid grid-cols-[0.85fr_1.15fr] gap-10">
        <div className="flex flex-col justify-center">
          <Kicker>{s.kicker}</Kicker>
          <Headline className="mt-5 text-[46px]">Trust creates <Mark>repeat usage.</Mark></Headline>
          <p className="mt-5 text-[18px] font-semibold max-w-[360px]" style={{ color: DECK.muted }}>{s.support}</p>
          <div className="mt-7 flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: DECK.accent }}>
              <RefreshCw className="h-6 w-6" style={{ color: DECK.ink }} />
            </div>
            <span className="font-display text-[18px] font-extrabold" style={{ color: DECK.ink }}>The WorkPay flywheel</span>
          </div>
        </div>
        <div className="relative">
          <svg viewBox="0 0 600 500" className="w-full h-full">
            <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(11,11,15,0.1)" strokeWidth="2" strokeDasharray="6 8" />
            {s.loop.map((_, i) => {
              const a = (i / s.loop.length) * Math.PI * 2 - Math.PI / 2;
              const a2 = ((i + 1) / s.loop.length) * Math.PI * 2 - Math.PI / 2;
              const mid = (a + a2) / 2;
              const ax = cx + Math.cos(mid) * R, ay = cy + Math.sin(mid) * R;
              return <circle key={i} cx={ax} cy={ay} r="4" fill={DECK.accentDeep} />;
            })}
            <clipPath id="loopCenterClip">
              <circle cx={cx} cy={cy} r="60" />
            </clipPath>
            <circle cx={cx} cy={cy} r="60" fill="#0A0E23" />
            <image href={ASSETS.logoMark} x={cx - 60} y={cy - 60} width="120" height="120" clipPath="url(#loopCenterClip)" preserveAspectRatio="xMidYMid slice" />
          </svg>
          {s.loop.map((node, i) => {
            const a = (i / s.loop.length) * Math.PI * 2 - Math.PI / 2;
            const left = `${((cx + Math.cos(a) * R) / 600) * 100}%`;
            const top = `${((cy + Math.sin(a) * R) / 500) * 100}%`;
            return (
              <div key={node} className="absolute -translate-x-1/2 -translate-y-1/2 rounded-2xl px-4 py-2.5 text-[13px] font-bold text-center w-[150px] leading-tight"
                style={{ left, top, background: "#fff", border: `1px solid rgba(11,11,15,0.1)`, boxShadow: "0 12px 30px -16px rgba(11,11,15,0.3)", color: DECK.ink }}>
                {node}
              </div>
            );
          })}
        </div>
      </Pad>
    </>
  );
};

/* ============================== SLIDE 11 — TEAM ============================== */
const Team = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Pad className="flex flex-col">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-5 text-[46px]">Built by operators who <Mark>know this pain.</Mark></Headline>
      <div className="mt-9 flex-1 grid grid-cols-3 gap-6 items-stretch">
        {s.founders.map((f) => (
          <SoftCard key={f.name} className="px-8 py-8 flex flex-col">
            <Avatar initials={f.initials} />
            <h3 className="font-display text-[26px] font-extrabold mt-6" style={{ color: DECK.ink }}>{f.name}</h3>
            <p className="text-[15px] font-bold mt-1" style={{ color: DECK.accentDeep }}>{f.role}</p>
            <p className="text-[15px] font-medium mt-4 leading-relaxed" style={{ color: DECK.muted }}>{f.bio}</p>
          </SoftCard>
        ))}
      </div>
    </Pad>
  </>
);

/* ============================== SLIDE 12 — VISION / ASK ============================== */
const Vision = ({ s }) => (
  <>
    <Bg color="#fff" />
    <Blob className="h-[440px] w-[440px]" style={{ background: DECK.accent, opacity: 0.2, top: -140, left: -100 }} />
    <Pad className="grid grid-cols-[1.1fr_0.9fr] gap-14">
      <div className="flex flex-col justify-center">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-5 text-[50px]">We are building the <Mark>trust layer</Mark> for service businesses.</Headline>
        <p className="mt-5 text-[19px] font-semibold max-w-[540px]" style={{ color: DECK.muted }}>{s.subheadline}</p>
        <p className="mt-8 font-display text-[26px] font-extrabold" style={{ color: DECK.ink }}>
          Let’s rebuild how service businesses <Mark>get paid.</Mark>
        </p>
      </div>
      <div className="flex flex-col justify-center">
        <div className="rounded-3xl px-8 py-7" style={{ background: DECK.accent }}>
          <p className="text-[14px] font-bold uppercase tracking-[0.22em]" style={{ color: DECK.ink, opacity: 0.7 }}>Raising</p>
          <p className="font-display text-[40px] font-extrabold leading-tight" style={{ color: DECK.ink }}>{s.raising}</p>
        </div>
        <div className="mt-5 rounded-3xl px-8 py-7" style={{ background: DECK.ink }}>
          <p className="text-[13px] font-bold uppercase tracking-[0.22em]" style={{ color: DECK.accent }}>Use of funds</p>
          <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-2.5">
            {s.useOfFunds.map((u) => (
              <div key={u} className="flex items-start gap-2 text-[14px] font-semibold text-white leading-snug">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: DECK.accent }} /> {u}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Pad>
  </>
);

/* ============================== APPENDIX LAYOUTS ============================== */
const Divider = ({ s }) => (
  <>
    <Bg color={DECK.paleGreen} />
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-20">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-6 text-[64px]">{s.headline}</Headline>
      <p className="mt-5 text-[20px] font-medium" style={{ color: DECK.charcoal }}>{s.subheadline}</p>
    </div>
  </>
);

const AppendixStats = ({ s }) => (
  <>
    <Bg color="#fff" />
    <Pad className="flex flex-col">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-5 text-[44px] max-w-[860px]">{s.headline}</Headline>
      <div className="mt-9 flex-1 grid grid-cols-3 gap-6 items-stretch">
        {s.stats.map((st) => (
          <SoftCard key={st.label} className="px-8 py-8 flex flex-col justify-center">
            <span className="font-display text-[36px] font-extrabold leading-tight" style={{ color: DECK.ink }}>{st.value}</span>
            <div className="h-[5px] w-16 rounded-full mt-3" style={{ background: DECK.accent }} />
            <p className="mt-4 text-[16px] font-semibold leading-snug" style={{ color: DECK.charcoal }}>{st.label}</p>
          </SoftCard>
        ))}
      </div>
      {s.note && <p className="mt-6 text-[14px] font-medium" style={{ color: DECK.muted }}>{s.note}</p>}
    </Pad>
  </>
);

const AppendixTable = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Pad className="flex flex-col">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-5 text-[40px] max-w-[820px]">{s.headline}</Headline>
      <div className="mt-7 flex-1">
        <div className="rounded-3xl overflow-hidden h-full flex flex-col" style={{ border: "1px solid rgba(11,11,15,0.1)" }}>
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr]" style={{ background: DECK.ink }}>
            <div className="px-6 py-4 text-[15px] font-bold" style={{ color: "rgba(255,255,255,0.6)" }}>Capability</div>
            {s.columns.map((c, i) => (
              <div key={c} className="px-6 py-4 text-[15px] font-extrabold text-center" style={{ color: i === 2 ? DECK.accent : "#fff" }}>{c}</div>
            ))}
          </div>
          {s.rows.map((r, idx) => (
            <div key={r.label} className="grid grid-cols-[1.5fr_1fr_1fr_1fr] flex-1" style={{ background: idx % 2 ? DECK.grey : "#fff" }}>
              <div className="px-6 flex items-center text-[15px] font-bold" style={{ color: DECK.ink }}>{r.label}</div>
              {["a", "b", "c"].map((k) => (
                <div key={k} className="flex items-center justify-center" style={k === "c" ? { background: DECK.paleGreen } : {}}>
                  {r[k] ? <Check className="h-5 w-5" style={{ color: DECK.accentDeep }} /> : <X className="h-5 w-5" style={{ color: "rgba(11,11,15,0.22)" }} />}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Pad>
  </>
);

const AppendixPanel = ({ s }) => (
  <>
    <Bg color="#fff" />
    <Pad className="grid grid-cols-[1.15fr_0.85fr] gap-12">
      <div className="flex flex-col justify-center">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-5 text-[42px] max-w-[560px]">{s.headline}</Headline>
        <ul className="mt-7 space-y-3.5">
          {s.points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-[18px] font-semibold" style={{ color: DECK.charcoal }}>
              <Check className="h-5 w-5 mt-0.5 shrink-0" style={{ color: DECK.accentDeep }} /> {p}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-center">
        <div style={{ width: 270, height: 430, overflow: "hidden" }}>
          <div style={{ transform: "scale(0.9)", transformOrigin: "top left", width: 300 }}>
            <PhoneMock variant={s.panelVariant} />
          </div>
        </div>
      </div>
    </Pad>
  </>
);

const AppendixRoadmap = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Pad className="flex flex-col">
      <Kicker>{s.kicker}</Kicker>
      <Headline className="mt-5 text-[42px] max-w-[820px]">{s.headline}</Headline>
      <div className="mt-9 flex-1 grid grid-cols-3 gap-6 items-stretch">
        {s.phases.map((ph, i) => (
          <div key={ph.title} className="rounded-3xl px-8 py-8 flex flex-col"
            style={i === 0 ? { background: DECK.ink } : { background: "#fff", border: "1px solid rgba(11,11,15,0.1)" }}>
            <span className="inline-flex self-start rounded-full px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.18em]"
              style={{ background: DECK.accent, color: DECK.ink }}>{ph.tag}</span>
            <h3 className="font-display text-[24px] font-extrabold mt-5" style={{ color: i === 0 ? "#fff" : DECK.ink }}>{ph.title}</h3>
            <ul className="mt-4 space-y-2.5">
              {ph.items.map((it) => (
                <li key={it} className="flex items-start gap-2.5 text-[15px] font-semibold leading-snug" style={{ color: i === 0 ? "rgba(255,255,255,0.9)" : DECK.charcoal }}>
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: DECK.accent }} /> {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Pad>
  </>
);

const Traction = ({ s }) => (
  <>
    <Bg color={DECK.offwhite} />
    <Blob className="h-[380px] w-[380px]" style={{ background: DECK.accent, opacity: 0.18, top: -100, right: 180 }} />
    <Pad className="grid grid-cols-[1fr_1fr] gap-14">
      <div className="flex flex-col">
        <Kicker>{s.kicker}</Kicker>
        <Headline className="mt-5 text-[42px] max-w-[540px]">{s.headline}</Headline>
        <div className="mt-8 flex items-end gap-3">
          <span className="font-display font-extrabold leading-none text-[88px]" style={{ color: DECK.ink }}>{s.number}</span>
        </div>
        <div className="h-[6px] w-[260px] rounded-full mt-3" style={{ background: DECK.accent }} />
        <p className="mt-4 text-[18px] font-semibold max-w-[440px]" style={{ color: DECK.charcoal }}>{s.numberLabel}</p>
        <div className="mt-auto grid grid-cols-2 gap-4">
          {s.stats.map((st) => (
            <div key={st.label} className="rounded-2xl px-6 py-5" style={{ background: "#fff", border: "1px solid rgba(11,11,15,0.1)" }}>
              <p className="font-display text-[32px] font-extrabold leading-none" style={{ color: DECK.ink }}>{st.value}</p>
              <p className="mt-2 text-[14px] font-semibold leading-snug" style={{ color: DECK.muted }}>{st.label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col justify-center">
        <div className="rounded-[2rem] p-10" style={{ background: DECK.ink }}>
          <CircleDollarSign className="h-9 w-9" style={{ color: DECK.accent }} />
          <p className="font-display text-[29px] font-extrabold leading-[1.16] mt-5" style={{ color: "#fff" }}>
            “{s.story.quote}”
          </p>
          <p className="mt-5 text-[16px] font-medium leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>{s.story.body}</p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {s.bottom.map((b) => (
              <span key={b} className="rounded-full px-4 py-2 text-[14px] font-bold" style={{ background: "rgba(193,255,114,0.16)", color: DECK.accent }}>{b}</span>
            ))}
          </div>
        </div>
        <p className="mt-4 text-[13px] font-medium" style={{ color: DECK.muted }}>{s.note}</p>
      </div>
    </Pad>
  </>
);

const LAYOUTS = {
  cover: Cover,
  problem: Problem,
  caseStudy: CaseStudy,
  marketPain: MarketPain,
  coreTruth: CoreTruth,
  solution: Solution,
  howItWorks: HowItWorks,
  product: Product,
  businessModel: BusinessModel,
  growthLoop: GrowthLoop,
  traction: Traction,
  team: Team,
  vision: Vision,
  divider: Divider,
  appendixStats: AppendixStats,
  appendixTable: AppendixTable,
  appendixPanel: AppendixPanel,
  appendixRoadmap: AppendixRoadmap,
};

export const SlideRenderer = ({ slide, index = 0, total = 1, showMeta = true }) => {
  const Layout = LAYOUTS[slide.type] || Problem;
  const onDark = false;
  return (
    <div className="relative w-full h-full overflow-hidden" data-testid={`slide-${slide.id}`}>
      <Layout s={slide} />
      {showMeta && slide.type !== "cover" && <SlideMeta index={index} total={total} onDark={onDark} />}
    </div>
  );
};

export default SlideRenderer;
